import {useLayoutEffect,type RefObject} from 'react';

/** Keep an absolute menu inside both its clipping panel and the visible viewport. */
export function useBoundedMenu(
  open:unknown,
  containerRef:RefObject<HTMLElement|null>,
  menuRef:RefObject<HTMLElement|null>,
  anchorRef:RefObject<HTMLElement|null>,
){
  useLayoutEffect(()=>{
    const container=containerRef.current,menu=menuRef.current,anchor=anchorRef.current;
    if(!open||!container||!menu||!anchor)return;
    const viewport=window.visualViewport;
    function place(){
      if(!container||!menu||!anchor)return;
      const parent=menu.offsetParent;
      if(!(parent instanceof HTMLElement))return;
      const bounds=container.getBoundingClientRect(),trigger=anchor.getBoundingClientRect();
      const viewportTop=viewport?.offsetTop??0,viewportBottom=viewportTop+(viewport?.height??window.innerHeight);
      const minimum=Math.max(bounds.top+container.clientTop,viewportTop)+8;
      const maximum=Math.min(bounds.top+container.clientTop+container.clientHeight,viewportBottom)-8;
      const available=Math.max(0,maximum-minimum);
      // Apply the cap before measuring: nonshrinking items retain their full scroll height.
      menu.style.maxHeight=`${available}px`;
      const height=Math.min(available,menu.scrollHeight+menu.offsetHeight-menu.clientHeight);
      const below=trigger.bottom+4,above=trigger.top-4-height;
      const preferred=below+height<=maximum?below:above>=minimum?above:below;
      const top=Math.max(minimum,Math.min(preferred,maximum-height));
      menu.style.top=`${top-parent.getBoundingClientRect().top-parent.clientTop+parent.scrollTop}px`;
    }
    place();
    const observer=new ResizeObserver(place);
    observer.observe(container);observer.observe(menu);observer.observe(anchor);
    // Scroll inside the menu must not move it or reset the user's scroll position.
    const onScroll=(event:Event)=>{if(event.target instanceof Node&&menu.contains(event.target))return;place();};
    window.addEventListener('resize',place);
    window.addEventListener('scroll',onScroll,true);
    viewport?.addEventListener('resize',place);viewport?.addEventListener('scroll',place);
    return ()=>{
      observer.disconnect();window.removeEventListener('resize',place);window.removeEventListener('scroll',onScroll,true);
      viewport?.removeEventListener('resize',place);viewport?.removeEventListener('scroll',place);
    };
  },[open,containerRef,menuRef,anchorRef]);
}
